import {LauncherItem, Platform} from "@variocube/app-api";
import {EventSupportingApp, generate} from "@variocube/app-common";
import * as React from "react";
import {render} from "react-dom";
import serviceIcon from "./service-icon.svg";
import logoIpsum from "./logoipsum.svg";
import {Main} from "./Main";
import {
    AppMode,
    Order,
    OrderOccupancyContent,
    OrderOccupancyType,
    OrderState,
    OrderType,
    Shops,
    ShopType
} from "./types";
import {AppContext, AppErrorBoundary} from "@variocube/cube-ui";
import {baseTheme, COLOR_PRIMARY} from "./themes";
import {LocalizationProvider, SUPPORTED_LANGUAGES} from "../i18n";

export default class extends EventSupportingApp {

    constructor(platform: Platform) {
        super(platform, serviceIcon, COLOR_PRIMARY, SUPPORTED_LANGUAGES, true);
    }

    private mode: AppMode = AppMode.Marketplace;
    private launcherItems: LauncherItem[] = [];

    computeLauncherItems() {
        return this.launcherItems;
    }

    private setMode = (mode: AppMode) => {
        this.mode = mode;
        this.updateLauncherItems();
    }

    private setLauncherItems = (launcherItems: LauncherItem[]) => {
        this.launcherItems = launcherItems;
        this.updateLauncherItems();
    }

    render(root: Element) {
        const appContext = {
            platform: this.platform,
            appEventSupport: this.eventSupport
        };
        render((
            <AppContext.Provider value={appContext}>
                <AppErrorBoundary appId={this.platform.appId} cubeId={this.platform.hostname}>
                    <LocalizationProvider app={this}>
                        <div className={baseTheme}>
                            <Main
                                onChangeMode={this.setMode}
                                onChangeLauncherItems={this.setLauncherItems}
                            />
                        </div>
                    </LocalizationProvider>
                </AppErrorBoundary>
            </AppContext.Provider>
        ), root);
    }

    async loadMockData() {

        const pickup: Order = {
            id: 1,
            orderNumber: "SCW001",
            orderDate: "2019-09-09T08:00:00Z",
            orderState: OrderState.WaitingForPickupByShop,
            orderType: OrderType.Outbound,
            customer: {
                phone: "+436507788311",
                lastName: "Sanchez",
                firstName: "Rick",
                email: "rick@galactic.co"
            },
            readyForDelivery: false,
            amount: 20,
            allowPickupDate: "now",
            internalNotes: "Caution: Explosive!",
            foreignId: "SOTF999"
        };

        const occupancy = await this.platform.occupy({
            accessCode: "12345",
            type: "M",
            content: {
                type: OrderOccupancyType.Inbound,
                order: pickup
            } as OrderOccupancyContent,
        });
        await this.platform.confirmOccupancy(occupancy.uuid);

        const deliver: Order = {
            id: 2,
            orderNumber: "SCW002",
            orderDate: "2019-09-09T09:00:00Z",
            orderState: OrderState.ReadyForDelivery,
            orderType: OrderType.Outbound,
            customer: {
                phone: "+436507788311",
                lastName: "Sanchez",
                firstName: "Rick",
                email: "rick@galactic.co"
            },
            amount: 9.99,
            readyForDelivery: true,
            storageCode: "store"
        };

        const pickupAcme: Order = {
            id: 3,
            orderNumber: "ACME003",
            orderDate: "2019-09-09T08:00:00Z",
            orderState: OrderState.WaitingForPickupByShop,
            orderType: OrderType.Inbound,
            customer: {
                phone: "+436507788311",
                lastName: "Sanchez",
                firstName: "Rick",
                email: "rick@galactic.co"
            },
            readyForDelivery: false,
            amount: 20,
            allowPickupDate: "now",
            internalNotes: "Caution: Explosive!",
            foreignId: "SOTF123"
        };
        const occupancyAcme = await this.platform.occupy({
            accessCode: "787878",
            type: "Any",
            content: {
                type: OrderOccupancyType.Inbound,
                order: pickupAcme
            } as OrderOccupancyContent,
        });
        await this.platform.confirmOccupancy(occupancyAcme.uuid);


        const acmeShops = generate(3, (ordinal: number) => ({
            ["acme" + ordinal]: {
                id: "acme" + ordinal,
                name: "ACME Inc. " + ordinal,
                logoUrl: "http://acmelogos.com/images/logo-3.svg",
                accessCode: "123",
                defaultBoxTypeShop: "M",
                orders: [pickupAcme],
                shopType: ShopType.Goods,
                offer: {
                    shortDescription: "Anvils, fireworks, pianos",
                    longDescription: "All the supplies you will ever need to catch the Road Runner. Check out our robust anvils. Use fireworks or other explosives to make a bang. Dropping our carefully manufactured pianos guarantees a nice sound on impact."
                },
                orderOptions: {
                    email: "order@acme.com",
                    phone: "+1 555 420 69",
                    app: "https://app.digitalermarktplatz.com/acme" + ordinal,
                    url: "https://shop.acme.com",
                }
            }
        })).reduce((acc, next) => ({...acc, ...next}), {});

        const single = Boolean(this.platform.getArg("single"));

        return {
            signUpUrl: "http://localhost:5000/api/customer",
            marketplace: {
                id: "variocube",
                title: "VariocubeMarktplatz",
                logoUrl: "https://variocube-documents.s3-eu-west-1.amazonaws.com/variocube.order/variocube/variocube-logo.svg"
            },
            shops: {
                easyApotheke: {
                    id: "easyApotheke",
                    name: "easyApotheke",
                    logoUrl: "https://variocube-documents.s3.eu-west-1.amazonaws.com/variocube.order/easyApotheke/logo.png",
                    accessCode: "999",
                    defaultBoxTypeShop: "M",
                    orders: [
                        pickup,
                        deliver
                    ],
                    offer: {
                        shortDescription: "einfach viel drin",
                        longDescription: "Wir haben ein innovatives und bis heute in Deutschland einzigartiges Systemapotheken-Konzept unter dem Markennamen „easyApotheke“ entwickelt, welches sich vor allem an den Bedürfnissen des Kunden orientiert."
                    },
                    orderOptions: {
                        app: "https://textilreiniger.info/zimmermann",
                    },
                    shopType: ShopType.Goods,
                    customersManuallyConfirmPickup: true,
                },
                /*
                zimmermann: {
                    id: "zimmermann",
                    name: "LaunDryClean Corp.",
                    logoUrl: "https://variocube-documents.s3-eu-west-1.amazonaws.com/variocube.order/zimmermann/logo.svg",
                    accessCode: "999",
                    defaultBoxTypeShop: "M",
                    orders: [
                        pickup,
                        deliver
                    ],
                    offer: {
                        shortDescription: "Laundry and Dry-Cleaning Service",
                        longDescription: "Fast and reliable dry-cleaning and laundry service. Our tailor service does alterations and repairs."
                    },
                    orderOptions: {
                        app: "https://textilreiniger.info/zimmermann",
                    },
                    shopType: ShopType.DryCleaning,
                    customersManuallyConfirmPickup: true,
                },*/
                ...(!single && {
                    phonerep: {
                        id: "phonerep",
                        name: "Phone Repair Ltd.",
                        logoUrl: "https://api.digitalermarktplatz.com/storage/shops/February2021/41bdbbe7-fcb5-47ce-9b9d-7d0b04f6f19b_1612902556.jpg",
                        accessCode: "555",
                        defaultBoxTypeShop: "M",
                        shopType: ShopType.Services,
                        orders: [
                            pickup,
                            deliver
                        ],
                        offer: {
                            shortDescription: "Phone repair and sale",
                            longDescription: "Cheap display and battery change. Best new phones for best price."
                        },
                        orderOptions: {
                            phone: "0660 91827344",
                            email: "service@phonerepair.ltd",
                        }
                    },
                    loremIpsum: {
                        id: "loremIpsum",
                        name: "Lorem Ipsum",
                        logoUrl: logoIpsum,
                        accessCode: "555",
                        defaultBoxTypeShop: "M",
                        orders: [],
                        shopType: ShopType.Services,
                        offer: {
                            shortDescription: "Lorem ipsum dolor sit amet",
                            longDescription: "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur. Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum."
                        },
                        orderOptions: {
                            email: "order@loremipsum.com",
                            phone: "+1 555 420 69",
                            app: "https://app.digitalermarktplatz.com/loremipsum",
                            url: "https://shop.loremipsum.com",
                        }
                    },
                    nologo: {
                        id: "nologo",
                        name: "No Logo Shop",
                        accessCode: "1234",
                        defaultBoxTypeShop: "M",
                        orders: [],
                        offer: {
                            shortDescription: "Logos & More",
                            longDescription: "Best logos in the world."
                        },
                        orderOptions: {
                            phone: "+43 123 34563456",
                            email: "shop@nologo.com"
                        },
                        shopType: ShopType.DryCleaning,
                    },
                    invalidLogo: {
                        id: "invalidLogo",
                        name: "Invalid Logo Shop",
                        accessCode: "404",
                        defaultBoxTypeShop: "M",
                        logoUrl: "https://this-logo-does-not.exist/logo.jpg",
                        orders: [],
                        offer: {
                            shortDescription: "Invalid Logos",
                            longDescription: "Broken links everywhere"
                        },
                        orderOptions: {
                            phone: "+43 123 34563456",
                            email: "shop@invalidlogo.com"
                        },
                        shopType: ShopType.Goods,
                    },
                    apotheke: {
                        id: "apotheke",
                        name: "Apotheke",
                        accessCode: "484848",
                        defaultBoxTypeShop: "M",
                        logoUrl: "https://variocube-documents.s3.eu-west-1.amazonaws.com/variocube.order/Apotheke_Stoppenberg/LOGO_Apotheke_am_Stoppenberg.png",
                        orders: [],
                        offer: {
                            shortDescription: "Apotheke",
                            longDescription: "Apotheke"
                        },
                        orderOptions: {
                            phone: "+43 123 34563456",
                            email: "shop@Apotheke.com"
                        },
                        shopType: ShopType.Goods,
                    },
                    ...acmeShops,
                }),
            } as Shops
        };
    }
}




